import React, { useRef, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Sidebar.css";
import rdvImg from "../../assets/rdv-logoW.png";
import rdvBallImg from "../../assets/rdv-logoBall.png";
import { useFileContext } from "../../ContextAPI/FileContext";
import { useUserPermissions } from "../../hooks/useUserPermissions";
import PermissionGate from "../PermissionGate/PermissionGate";
import { useDownloads } from "../../contexts/DownloadsContext";
import { useUploads } from "../../contexts/UploadsContext";
import SidebarSettings from "./SidebarSettings";
import { baseApi } from "../../API/api";
import { useProjectOptional } from "../../contexts/ProjectContext";
import { canSeeMultiProjectRuns } from "./RunQueue/runQueueAccess";
import { ServerPermissionGate } from "../../utils/serverPermissions";
import { useAuthOptions } from "../../utils/authOptions";

const Sidebar = ({
  variant = "rail",
  open = false,
  onClose,
  view = "nav",
  onViewChange = () => {},
  // Drill back up one drawer level. AuthenticatedLayout supplies a handler
  // that goes through the history stack (so the on-screen arrow behaves
  // exactly like the OS back gesture); the default keeps standalone renders
  // — and the desktop rail — working.
  onBack,
}) => {
  const handleBack = onBack || (() => onViewChange("nav"));
  // 'drawer' = mobile off-canvas mode (rendered by AuthenticatedLayout on
  // ≤768px). Default 'rail' keeps the desktop behavior byte-identical.
  const isDrawer = variant === "drawer";
  const location = useLocation();
  const currentPath = location.pathname;
  const isProjectsRoute =
    currentPath === "/projects" || currentPath.startsWith("/projects/");
  const { logout, userEmail } = useFileContext();
  const { permissions, loading } = useUserPermissions();

  // "Multi-project runs" is offered to admins and to users with more than one
  // RSA project. The list is the app-wide cached one (a no-op once loaded).
  const projectCtx = useProjectOptional();
  const myProjects = projectCtx?.projects;
  const loadMyProjects = projectCtx?.loadProjects;
  useEffect(() => {
    if (!isDrawer && loadMyProjects) loadMyProjects();
  }, [isDrawer, loadMyProjects]);
  const showMultiProjectRuns = !isDrawer && canSeeMultiProjectRuns(permissions, myProjects);
  // User management lives in Account settings (Cognito group Admin), so Admins need
  // the desktop Settings button even while text messages are off.
  const isAdmin = (permissions?.groups || []).some(
    (g) => String(g).trim().toLowerCase() === "admin");

  // Debug: Log permissions when they change
  useEffect(() => {
    if (permissions) {
      console.log("[Sidebar] Permissions loaded:", permissions);
      console.log("[Sidebar] User groups:", permissions.groups);
      console.log(
        "[Sidebar] Has developers group:",
        permissions.groups?.includes("developers"),
      );
    }
  }, [permissions]);
  const {
    togglePopup,
    downloads,
    isPopupOpen,
    closePopup: closeDownloadsPopup,
  } = useDownloads();
  const {
    togglePopup: toggleUploadsPopup,
    uploads,
    isPopupOpen: isUploadsPopupOpen,
    closePopup: closeUploadsPopup,
  } = useUploads();

  const handleToggleUploads = () => {
    if (!isUploadsPopupOpen) closeDownloadsPopup();
    toggleUploadsPopup();
  };

  const handleToggleDownloads = () => {
    if (!isPopupOpen) closeUploadsPopup();
    togglePopup();
  };
  const emailRef = useRef(null);
  const sidebarListRef = useRef(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const navigate = useNavigate();
  const smsOn = useAuthOptions()?.sms === true;
  const [useCollapsedLogo, setUseCollapsedLogo] = useState(true);
  const [dynamicWidth, setDynamicWidth] = useState(() => {
    if (typeof window === "undefined") return 80;
    const scale =
      window.innerHeight >= 1800 ? 1.8 : window.innerHeight >= 1400 ? 1.3 : 1;
    return Math.round(80 * scale);
  });

  const getSidebarScale = () => {
    if (typeof window === "undefined") {
      return 1;
    }

    if (window.innerHeight >= 1800) {
      return 1.8;
    }

    if (window.innerHeight >= 1400) {
      return 1.3;
    }

    return 1;
  };

  // Automatically collapse sidebar when /view route is opened
  useEffect(() => {
    if (currentPath.endsWith("/view")) {
      if (!isCollapsed) {
        setIsCollapsed(true);
        setUseCollapsedLogo(true);
      }
    }
  }, [currentPath, isCollapsed]);

  // Calculate dynamic width based on longest li text width and collapsed state
  const calculateWidth = () => {
    if (isDrawer) return; // drawer width is pure CSS — bypass the JS machinery
    const scale = getSidebarScale();

    if (isCollapsed) {
      setDynamicWidth(Math.round(80 * scale)); // Collapsed width for icons only
      return;
    }

    if (sidebarListRef.current) {
      const listItems = sidebarListRef.current.querySelectorAll("li a span");
      let maxWidth = 0;

      const baseWidth = 235 * scale; // Base width in pixels
      const padding = 80 * scale; // Reduced padding for other elements (avatar, margins, etc.)
      const minWidth = baseWidth;
      const maxWidthLimit = 600 * scale; // Maximum width to prevent excessive expansion

      // Create a temporary span to measure text width
      const tempSpan = document.createElement("span");
      tempSpan.style.fontFamily =
        "Montserrat, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
      tempSpan.style.fontSize = `${14 * scale}px`; // Match the CSS font-size for nav links
      tempSpan.style.fontWeight = "300"; // Match the CSS font-weight
      tempSpan.style.visibility = "hidden";
      tempSpan.style.position = "absolute";
      tempSpan.style.whiteSpace = "nowrap";

      // Measure each list item's text width
      listItems.forEach((span) => {
        if (span.textContent) {
          tempSpan.textContent = span.textContent;
          document.body.appendChild(tempSpan);
          const textWidth = tempSpan.offsetWidth;
          document.body.removeChild(tempSpan);
          maxWidth = Math.max(maxWidth, textWidth);
        }
      });

      const calculatedWidth = Math.min(
        Math.max(maxWidth + padding, minWidth),
        maxWidthLimit,
      );
      setDynamicWidth(calculatedWidth);
    }
  };

  // Calculate width when collapsed state changes
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      calculateWidth();
    }, 0);

    const handleResize = () => {
      calculateWidth();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [isCollapsed, currentPath]); // Recalculate when collapsed state or viewport changes

  // Drawer mode: close on navigation so tapping a link dismisses the menu.
  useEffect(() => {
    if (isDrawer) {
      onClose?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const handleLogout = () => {
    logout();
  };

  const toggleSidebar = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    // Immediately switch logo to match target state
    setUseCollapsedLogo(next);
  };

  // Function to get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();

    if (hour >= 6 && hour < 12) {
      return "Good morning!";
    } else if (hour >= 12 && hour < 18) {
      return "Good afternoon!";
    } else {
      return "Good evening!";
    }
  };

  // Drawer renders expanded (icons + labels) regardless of the rail's
  // collapsed state, with CSS-driven width instead of the measured one.
  const collapsedVisual = isDrawer ? false : isCollapsed;

  // Drill-down state only exists in the drawer; the rail always shows nav.
  const showSettings = isDrawer && view === "settings";

  return (
    <>
      {isDrawer && open && (
        <div
          className="sidebar-drawer-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        className={`sidebar ${collapsedVisual ? "collapsed" : ""} ${isDrawer ? "sidebar--drawer" : ""} ${isDrawer && open ? "is-open" : ""}`}
        style={isDrawer ? undefined : { width: `${dynamicWidth}px` }}
      >
        <div className="sidebarHeader file-selector">
          <div className="sidebarHeaderLogout">
            <div className="header-content">
              <div className="sidebar-avatar">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="7"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="user-info">
                <p className="user-name">{getTimeBasedGreeting()}</p>
                <p className="user-email" ref={emailRef}>
                  {(userEmail || "shafrir.kashi@rdvsystems.com").split("@")[0]}
                </p>
              </div>
            </div>
            {/* Toggle Button (rail only — drawer is always expanded) */}
            {!isDrawer && (
              <button
                className="sidebar-toggle"
                onClick={toggleSidebar}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <i
                  className={`fas ${isCollapsed ? "fa-chevron-right" : "fa-chevron-left"}`}
                ></i>
              </button>
            )}
          </div>
          <ul ref={sidebarListRef}>
            {/* Basic user access - everyone can see these */}
            <li>
              <Link
                to="/projects"
                className={isProjectsRoute ? "active" : ""}
                aria-label={isCollapsed ? "Projects" : undefined}
              >
                <i className="fas fa-folder-open icon"></i>
                <span>Projects</span>
              </Link>
            </li>
            {/* Multi-project runs — the one run queue (desktop only). Admins and
                users with more than one RSA project; the view itself scopes
                non-admins to their own projects' entries (RunQueue/runQueueAccess.js). */}
            {showMultiProjectRuns && (
              <li>
                <Link
                  to="/multi-project-runs"
                  className={currentPath === "/multi-project-runs" ? "active" : ""}
                  aria-label={isCollapsed ? "Multi-project runs" : undefined}
                >
                  <i className="fa-solid fa-layer-group icon"></i>
                  <span>Multi-project runs</span>
                </Link>
              </li>
            )}
            {/* Developers group access only */}
            <PermissionGate group="Admin">
              <li>
                <Link
                  to="/RSA-Analysis"
                  className={currentPath === "/RSA-Analysis" ? "active" : ""}
                  aria-label={isCollapsed ? "RSA Analysis" : undefined}
                >
                  <i className="fa-solid fa-chart-simple icon"></i>
                  <span>RSA Analysis</span>
                </Link>
              </li>
            </PermissionGate>

            {/* Safety Dashboard (accounts granted view_safety_overview on the server; desktop only) */}
            {!isDrawer && (
              <ServerPermissionGate permission="view_safety_overview">
                <li>
                  <Link
                    to="/safety-dashboard"
                    className={currentPath === "/safety-dashboard" ? "active" : ""}
                    aria-label={isCollapsed ? "Safety Dashboard" : undefined}
                  >
                    <i className="fa-solid fa-shield-halved icon"></i>
                    <span>Safety Dashboard</span>
                  </Link>
                </li>
              </ServerPermissionGate>
            )}

            {/* Usage & Cost dashboard — Super Users + Administrators (desktop only) */}
            {!isDrawer && (
              <PermissionGate permission="view_usage_assigned">
                <li>
                  <Link
                    to="/usage-dashboard"
                    className={
                      currentPath === "/usage-dashboard" ? "active" : ""
                    }
                    aria-label={isCollapsed ? "Usage Dashboard" : undefined}
                  >
                    <i className="fa-solid fa-gauge-high icon"></i>
                    <span>Usage Dashboard</span>
                  </Link>
                </li>
              </PermissionGate>
            )}

            {/* Scheduled PDF reports — Super Users + Administrators (desktop only) */}
            {!isDrawer && (
              <PermissionGate permission="schedule_usage_reports">
                <li>
                  <Link
                    to="/scheduled-reports"
                    className={
                      currentPath === "/scheduled-reports" ? "active" : ""
                    }
                    aria-label={isCollapsed ? "Scheduled Reports" : undefined}
                  >
                    <i className="fa-solid fa-envelope-open-text icon"></i>
                    <span>Scheduled Reports</span>
                  </Link>
                </li>
              </PermissionGate>
            )}

            {/* Developers group access only */}
            {/* <PermissionGate group="developers">
            <li>
              <Link 
                to='/magic-fixer' 
                className={currentPath === '/magic-fixer' ? 'active' : ''}
                title={isCollapsed ? 'Magic Fixer' : ''}
              >
                <i className='fas fa-magic icon'></i>
                {!isCollapsed && <span>Magic Fixer</span>}
              </Link>
            </li>
          </PermissionGate> */}
          </ul>
        </div>

        {/* Settings takes the profile buttons' slot rather than pushing them
          down: the nav stays reachable above it, and the bottom-anchored
          position is unchanged, so opening Settings shifts nothing else. */}
        {showSettings ? (
          <SidebarSettings onBack={handleBack} />
        ) : (
          <div className="user-profile-buttons">
            {!isDrawer && (
              <button
                className={`profile-button uploads ${isUploadsPopupOpen ? "has-uploads" : ""}`}
                onClick={handleToggleUploads}
                aria-label={isCollapsed ? "Uploads" : undefined}
              >
                <div className="downloads-button-content">
                  <i className="fas fa-upload"></i>
                  <span>Uploads</span>
                </div>
                {(() => {
                  // Sum of files still pending across all in-flight batches.
                  // Starts at the batch's total file count and decrements as each
                  // file reaches a terminal state (success/error/cancelled/skipped).
                  // Excludes interrupted/failed/locked batches — they're stalled,
                  // not actively making progress.
                  const remaining = uploads.reduce((sum, u) => {
                    if (u.status !== "uploading" && u.status !== "finalizing")
                      return sum;
                    const files = u.files || [];
                    const statuses = u.fileStatuses || {};
                    return (
                      sum +
                      files.filter((_, i) => {
                        const s = statuses[i];
                        return (
                          s !== "success" &&
                          s !== "error" &&
                          s !== "cancelled" &&
                          s !== "skipped"
                        );
                      }).length
                    );
                  }, 0);
                  return remaining > 0 ? (
                    <span className="downloads-badge">{remaining}</span>
                  ) : null;
                })()}
              </button>
            )}
            <button
              className={`profile-button downloads ${isPopupOpen ? "has-downloads" : ""}`}
              onClick={handleToggleDownloads}
              aria-label={isCollapsed ? "Downloads" : undefined}
            >
              <div className="downloads-button-content">
                <i className="fas fa-download"></i>
                <span>Downloads</span>
              </div>
              {downloads.length > 0 && (
                <span className="downloads-badge">{downloads.length}</span>
              )}
            </button>

            {/* Settings: the phone drawer drills into its Settings page; the desktop
            rail opens the Account settings page - once text messages are on (Sign-in
            methods), or for Admins (User management). */}
            {(isDrawer || smsOn || isAdmin) && (
              <button
                className="profile-button account"
                onClick={isDrawer ? () => onViewChange("settings") : () => navigate("/account")}
                aria-label={isCollapsed && !isDrawer ? "Settings" : undefined}
              >
                <div className="downloads-button-content">
                  <i className="fa-solid fa-gear"></i>
                  <span>Settings</span>
                </div>
              </button>
            )}

            <button
              className="profile-button logout"
              onClick={handleLogout}
              aria-label={isCollapsed ? "Logout" : undefined}
            >
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
          </div>
        )}

        {/* Logo at the bottom (drawer always shows the wide logo) */}
        <div className="sidebar-footer">
          <div className="logo">
            <img
              src={rdvImg}
              alt="Logo"
              className={`logo-wide ${useCollapsedLogo && !isDrawer ? "is-hidden" : "is-shown"}`}
            />
            <img
              src={rdvBallImg}
              alt="Logo"
              className={`logo-ball ${useCollapsedLogo && !isDrawer ? "is-shown" : "is-hidden"}`}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
